import { Ban } from "lucide-react";
import { LegalCompanyInfo } from "@/components/LegalCompanyInfo";
import { useTosVersion } from "@/components/TermsBody";
import { useReleaseDateLabel } from "@/hooks/useReleaseMetadata";

/**
 * Compliance Roadmap Items 15 & 17 — User-Generated Content Rules.
 *
 * The prohibited-content list + user attestations that anyone submitting
 * User Content must accept. Substantive obligations are already covered
 * in Terms §12; this page is the plain-English reference version.
 */
function S({ n, title, id, children }) {
  return (
    <section id={id} className="space-y-2.5">
      <h2 className="font-display text-lg text-white tracking-[0.04em] flex gap-2.5">
        <span className="text-rose-300 font-mono">{n}.</span> {title}
      </h2>
      <div className="text-white/65 text-[13.5px] leading-relaxed space-y-2.5">{children}</div>
    </section>
  );
}

const Em = ({ children }) => <span className="text-white font-medium">{children}</span>;

export default function UgcRulesPage() {
  const version = useTosVersion();
  const releaseDate = useReleaseDateLabel();
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="ugc-rules-page">
      <div className="hud-pill rounded-2xl p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-xl bg-rose-400/15 border border-rose-300/30 flex items-center justify-center shrink-0">
            <Ban className="h-5 w-5 text-rose-300" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-rose-300/80">Legal · User Content</div>
            <h1 className="font-display text-3xl sm:text-4xl text-white tracking-[0.05em]">UGC &amp; COMMUNITY RULES</h1>
          </div>
        </div>
        <p data-testid="ugc-current-version" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-8">
          Playden Inc. · Version {version} · {releaseDate}
        </p>

        <div className="space-y-8" data-testid="ugc-document">
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/[0.05] p-4 text-[13px] leading-relaxed text-rose-100/90">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-rose-300/90 mb-1">Zero Tolerance</div>
            Anything on this list results in <Em>immediate content removal, account termination, forfeiture of Virtual Items, and reporting to law enforcement where required</Em>. If in doubt, do not upload it.
          </div>

          <S n="1" title="Strictly prohibited content" id="prohibited">
            <p>You may not submit, upload, prompt, request, generate, host, display, or transmit any content that:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Depicts or sexualizes any person under the age of 21 (or any figure that appears or is presented as under 21), whether real, fictional, drawn, painted, computer-generated, AI-generated, or otherwise.</li>
              <li>Involves non-consenting subjects, coercion, incapacitation, trafficked persons, or any conduct a reasonable person would understand to be non-consensual.</li>
              <li>Involves bestiality, incest between real biological relatives, necrophilia, or any other conduct that is unlawful where you or the intended audience are located.</li>
              <li>Depicts real, identifiable persons without their documented, informed consent — including &quot;deepfakes,&quot; face-swaps, or likeness-based content of any public or private individual.</li>
              <li>Contains real-world identifying information about any person (doxxing, addresses, phone numbers, IDs, medical records).</li>
              <li>Constitutes revenge porn or the non-consensual sharing of intimate imagery.</li>
              <li>Promotes, glorifies, or facilitates violence, self-harm, suicide, terrorism, human trafficking, drug trafficking, or the sexual exploitation of minors.</li>
              <li>Contains hate speech, slurs, threats, or harassment targeted at protected classes.</li>
              <li>Infringes copyright, trademark, right of publicity, or any other third-party intellectual-property or contractual right.</li>
              <li>Contains malware, phishing links, or code intended to compromise the Service or its users.</li>
              <li>Is illegal to possess, produce, distribute, or view where you or the intended audience are located.</li>
            </ul>
          </S>

          <S n="2" title="Adult content — what IS allowed" id="allowed">
            <p>Playden is a 21+ platform. Adult content is permitted <Em>only if all of the following are true</Em>:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Every person depicted (real, drawn, or AI-generated) is <Em>at least 21 years of age</Em> and unambiguously presented as such (adult body, adult context, no schoolyards, no juvenile framing).</li>
              <li>The conduct depicted is <Em>consensual</Em> between all participants and lawful where the content originated and where it will be displayed.</li>
              <li>Any real, identifiable person has provided <Em>documented, informed consent</Em> to their depiction and to your submission of that depiction to Playden.</li>
              <li>You own or hold every right necessary to submit the content and to grant Playden the license described in Terms §12.</li>
            </ul>
          </S>

          <S n="3" title="User attestations" id="attestations">
            <p>Each time you submit User Content — including prompts, uploads, chat images, and print files — you attest that:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You are at least 21 years of age.</li>
              <li>All persons depicted are at least 21 years of age and consenting adults.</li>
              <li>You own or hold all rights required to submit the content.</li>
              <li>Your submission complies with Section 1 (Prohibited) and Section 2 (Allowed).</li>
              <li>You accept sole legal responsibility for the content and will indemnify Playden per Terms §20.</li>
            </ul>
          </S>

          <S n="4" title="Reporting content or users" id="reporting">
            <p>To report content or a user that violates these rules, use the <Em>in-Service Report button</Em> (available on card detail pages and coming to more surfaces) — one click opens a categorized report form. You can also email <Em>{"{{ABUSE_EMAIL}}"}</Em> with:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>A URL, card ID, user ID, or screenshot of the content.</li>
              <li>Which specific rule you believe was violated.</li>
              <li>Your account email (for follow-up) and, if you are the affected person, evidence supporting your claim.</li>
            </ul>
            <p>We review reports promptly and take action based on our findings; we may contact you for additional information. Reports made in bad faith or to harass another user are themselves violations and may result in enforcement against the reporter.</p>
          </S>

          <S n="5" title="Enforcement" id="enforcement">
            <p>Violations are handled per Terms §14 (Suspension, Termination, and Forfeiture). Serious or repeated violations, and any content covered by Section 1 above, result in immediate account termination, forfeiture of Virtual Items, and — where applicable — reporting to law enforcement or the National Center for Missing &amp; Exploited Children (NCMEC).</p>
          </S>

          <LegalCompanyInfo className="mt-4" testid="ugc-company-info" />
        </div>
      </div>
    </div>
  );
}
